import { useState } from 'react';

const AgentOrdering = () => {
  const [selectedForm, setSelectedForm] = useState<'business' | 'service' | 'sim'>('business');
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const [charCounts, setCharCounts] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const nigerianStates = [
    'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno',
    'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT Abuja', 'Gombe',
    'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara', 'Lagos',
    'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun', 'Oyo', 'Plateau', 'Rivers', 'Sokoto',
    'Taraba', 'Yobe', 'Zamfara'
  ];

  const serviceOptions = [
    'DSA (Direct Sales Agent)',
    'Corporate Services',
    'Financial Services',
    'Digital Marketing',
    'Skills Training',
    'Business Registration',
    'Tax Consultation',
    'Legal Services',
    'IT Support',
    'Other'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
    
    if (e.target instanceof HTMLTextAreaElement && e.target.maxLength) {
      setCharCounts((prev: any) => ({ ...prev, [name]: value.length }));
    }
  };

  const handleSubmit = async (e: React.FormEvent, formId: string, submitUrl: string) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const form = e.target as HTMLFormElement;
      const formDataToSubmit = new FormData(form);
      const urlEncodedData = new URLSearchParams(formDataToSubmit as any).toString();

      const response = await fetch(submitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData,
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({});
        setCharCounts({});
        setTimeout(() => {
          setIsOpen(false);
          setSubmitStatus(null);
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderAgentForm = () => (
    <form
      id="agent-application-form"
      data-readdy-form
      onSubmit={(e) => handleSubmit(e, 'agent-application-form', 'https://readdy.ai/api/form/d5193to9ei8qjpb9fqp0')}
      className="space-y-4"
    >
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
        <input
          type="text"
          name="fullName"
          value={formData.fullName || ''}
          onChange={handleInputChange}
          required
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
        <input
          type="date"
          name="dateOfBirth"
          value={formData.dateOfBirth || ''}
          onChange={handleInputChange}
          required
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
        <input
          type="tel"
          name="phoneNumber"
          value={formData.phoneNumber || ''}
          onChange={handleInputChange}
          required
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
        <input
          type="email"
          name="emailAddress"
          value={formData.emailAddress || ''}
          onChange={handleInputChange}
          required
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Residential Address *</label>
        <input
          type="text"
          name="residentialAddress"
          value={formData.residentialAddress || ''}
          onChange={handleInputChange}
          required
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Local Government Area (LGA) *</label>
        <input
          type="text"
          name="lga"
          value={formData.lga || ''}
          onChange={handleInputChange}
          required
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
        <select
          name="state"
          value={formData.state || ''}
          onChange={handleInputChange}
          required
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
        >
          <option value="">Select State</option>
          {nigerianStates.map((state) => (
            <option key={state} value={state}>{state}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Becoming an Agent *</label>
        <textarea
          name="reason"
          value={formData.reason || ''}
          onChange={handleInputChange}
          required
          maxLength={500}
          rows={4}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
        />
        <div className="text-xs text-gray-500 text-right mt-1">
          {charCounts.reason || 0}/500 characters
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Years of Experience in Similar Roles *</label>
        <select
          name="experience"
          value={formData.experience || ''}
          onChange={handleInputChange}
          required
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
        >
          <option value="">Select Experience</option>
          <option value="0-1">0–1 years</option>
          <option value="2-5">2–5 years</option>
          <option value="5+">5+ years</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Previous Similar Registration or Agent Roles *</label>
        <textarea
          name="previousRoles"
          value={formData.previousRoles || ''}
          onChange={handleInputChange}
          required
          maxLength={500}
          rows={4}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
        />
        <div className="text-xs text-gray-500 text-right mt-1">
          {charCounts.previousRoles || 0}/500 characters
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Service Area or Territory</label>
        <input
          type="text"
          name="preferredArea"
          value={formData.preferredArea || ''}
          onChange={handleInputChange}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
        />
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
      >
        {isSubmitting ? 'Submitting...' : 'Submit Application'}
      </button>
    </form>
  );

  const renderBusinessForm = () => (
    <form
      id="business-registration-form"
      data-readdy-form
      onSubmit={(e) => handleSubmit(e, 'business-registration-form', 'https://readdy.ai/api/form/d5193to9ei8qjpb9fqpg')}
      className="space-y-4"
    >
      <div className="border-b pb-4 mb-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Business Type</h3>
        <select
          name="businessType"
          value={formData.businessType || ''}
          onChange={handleInputChange}
          required
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
        >
          <option value="">Select Business Type</option>
          <option value="Business Name">Business Name</option>
          <option value="Inter-Liability">Inter-Liability</option>
          <option value="Incorporated Trustee">Incorporated Trustee</option>
        </select>
      </div>

      <div className="border-b pb-4 mb-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Applicant Details</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
            <input
              type="text"
              name="applicantFullName"
              value={formData.applicantFullName || ''}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
            <input
              type="tel"
              name="applicantPhone"
              value={formData.applicantPhone || ''}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              name="applicantEmail"
              value={formData.applicantEmail || ''}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Residential Address *</label>
            <input
              type="text"
              name="applicantAddress"
              value={formData.applicantAddress || ''}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">LGA *</label>
            <input
              type="text"
              name="applicantLGA"
              value={formData.applicantLGA || ''}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
            <select
              name="applicantState"
              value={formData.applicantState || ''}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            >
              <option value="">Select State</option>
              {nigerianStates.map((state) => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="border-b pb-4 mb-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Business Details</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Business Name *</label>
            <input
              type="text"
              name="businessName"
              value={formData.businessName || ''}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Business Description *</label>
            <textarea
              name="businessDescription"
              value={formData.businessDescription || ''}
              onChange={handleInputChange}
              required
              maxLength={500}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
            />
            <div className="text-xs text-gray-500 text-right mt-1">
              {charCounts.businessDescription || 0}/500 characters
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Business Address *</label>
            <input
              type="text"
              name="businessAddress"
              value={formData.businessAddress || ''}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nature of Business *</label>
            <input
              type="text"
              name="natureOfBusiness"
              value={formData.natureOfBusiness || ''}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Required Uploads</h3>
        <p className="text-sm text-gray-600 mb-2">Identification and supporting documents</p>
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
          <p className="text-sm text-gray-500">Uncollectable</p>
        </div>
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
      >
        {isSubmitting ? 'Submitting...' : 'Submit Registration Request'}
      </button>
    </form>
  );

  const renderServiceForm = () => (
    <form
      id="service-order-form"
      data-readdy-form
      onSubmit={(e) => handleSubmit(e, 'service-order-form', 'https://readdy.ai/api/form/d5193to9ei8qjpb9fqq0')}
      className="space-y-4"
    >
      <div className="border-b pb-4 mb-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Client Details</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              name="clientName"
              value={formData.clientName || ''}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
            <input
              type="tel"
              name="clientPhone"
              value={formData.clientPhone || ''}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              name="clientEmail"
              value={formData.clientEmail || ''}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Residential/Corporate Address *</label>
            <input
              type="text"
              name="clientAddress"
              value={formData.clientAddress || ''}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>
        </div>
      </div>

      <div className="border-b pb-4 mb-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Service Selection</h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Select Service *</label>
          <select
            name="serviceType"
            value={formData.serviceType || ''}
            onChange={handleInputChange}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
          >
            <option value="">Choose a service</option>
            {serviceOptions.map((service) => (
              <option key={service} value={service}>{service}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Service Description *</label>
        <textarea
          name="serviceDescription"
          value={formData.serviceDescription || ''}
          onChange={handleInputChange}
          required
          maxLength={500}
          rows={5}
          placeholder="Describe your requirements in detail..."
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
        />
        <div className="text-xs text-gray-500 text-right mt-1">
          {charCounts.serviceDescription || 0}/500 characters
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Optional Attachments</label>
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
          <p className="text-sm text-gray-500">Uncollectable</p>
        </div>
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
      >
        {isSubmitting ? 'Submitting...' : 'Submit Order'}
      </button>
    </form>
  );

  const renderSIMForm = () => (
    <form
      id="sim-registration-form"
      data-readdy-form
      onSubmit={(e) => handleSubmit(e, 'sim-registration-form', 'https://readdy.ai/api/form/d5193to9ei8qjpb9fqog')}
      className="space-y-4"
    >
      <div className="border-b pb-4 mb-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Section A: Personal Information</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName || ''}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth (DD/MM/YYYY) *</label>
            <input
              type="date"
              name="dateOfBirth"
              value={formData.dateOfBirth || ''}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Gender *</label>
            <div className="flex gap-6 mt-2">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="gender"
                  value="Male"
                  checked={formData.gender === 'Male'}
                  onChange={handleInputChange}
                  required
                  className="w-4 h-4 text-teal-600 focus:ring-teal-500"
                />
                <span className="ml-2 text-sm text-gray-700">Male</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="gender"
                  value="Female"
                  checked={formData.gender === 'Female'}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-teal-600 focus:ring-teal-500"
                />
                <span className="ml-2 text-sm text-gray-700">Female</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="gender"
                  value="Other"
                  checked={formData.gender === 'Other'}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-teal-600 focus:ring-teal-500"
                />
                <span className="ml-2 text-sm text-gray-700">Other</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Residential Address / Location *</label>
            <input
              type="text"
              name="residentialAddress"
              value={formData.residentialAddress || ''}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Local Government Area (LGA) *</label>
            <input
              type="text"
              name="lga"
              value={formData.lga || ''}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">State of Residence *</label>
            <select
              name="state"
              value={formData.state || ''}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            >
              <option value="">Select State</option>
              {nigerianStates.map((state) => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="border-b pb-4 mb-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Section B: Contact Information</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber || ''}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
            <input
              type="email"
              name="emailAddress"
              value={formData.emailAddress || ''}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Section C: Request Details</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason for SIM Registration / Purpose *</label>
            <textarea
              name="registrationReason"
              value={formData.registrationReason || ''}
              onChange={handleInputChange}
              required
              maxLength={500}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
            />
            <div className="text-xs text-gray-500 text-right mt-1">
              {charCounts.registrationReason || 0}/500 characters
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Prior Experience in Similar Registration / Related Services *</label>
            <div className="flex gap-6">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="priorExperience"
                  value="Yes"
                  checked={formData.priorExperience === 'Yes'}
                  onChange={handleInputChange}
                  required
                  className="w-4 h-4 text-teal-600 focus:ring-teal-500"
                />
                <span className="ml-2 text-sm text-gray-700">Yes</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="priorExperience"
                  value="No"
                  checked={formData.priorExperience === 'No'}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-teal-600 focus:ring-teal-500"
                />
                <span className="ml-2 text-sm text-gray-700">No</span>
              </label>
            </div>
          </div>

          {formData.priorExperience === 'Yes' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">If yes, describe briefly</label>
              <textarea
                name="experienceDescription"
                value={formData.experienceDescription || ''}
                onChange={handleInputChange}
                maxLength={500}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
              />
              <div className="text-xs text-gray-500 text-right mt-1">
                {charCounts.experienceDescription || 0}/500 characters
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Previous Registration with Similar Service *</label>
            <div className="flex gap-6">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="previousRegistration"
                  value="Yes"
                  checked={formData.previousRegistration === 'Yes'}
                  onChange={handleInputChange}
                  required
                  className="w-4 h-4 text-teal-600 focus:ring-teal-500"
                />
                <span className="ml-2 text-sm text-gray-700">Yes</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="previousRegistration"
                  value="No"
                  checked={formData.previousRegistration === 'No'}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-teal-600 focus:ring-teal-500"
                />
                <span className="ml-2 text-sm text-gray-700">No</span>
              </label>
            </div>
          </div>

          {formData.previousRegistration === 'Yes' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">If yes, provide details</label>
              <textarea
                name="registrationDetails"
                value={formData.registrationDetails || ''}
                onChange={handleInputChange}
                maxLength={500}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
              />
              <div className="text-xs text-gray-500 text-right mt-1">
                {charCounts.registrationDetails || 0}/500 characters
              </div>
            </div>
          )}
        </div>
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
      >
        {isSubmitting ? 'Submitting...' : 'Submit SIM Registration Request'}
      </button>
    </form>
  );

  return (
    <section id="become-agent" className="py-20 bg-gradient-to-br from-teal-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Order Our Services</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Choose the service you need and fill out the appropriate form
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {/* Form Type Selector */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
              <button
                onClick={() => {
                  setSelectedForm('business');
                  setFormData({});
                  setCharCounts({});
                  setSubmitStatus(null);
                }}
                className={`px-4 py-3 rounded-lg font-medium text-sm transition-all whitespace-nowrap ${
                  selectedForm === 'business'
                    ? 'bg-teal-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Business Registration
              </button>
              <button
                onClick={() => {
                  setSelectedForm('service');
                  setFormData({});
                  setCharCounts({});
                  setSubmitStatus(null);
                }}
                className={`px-4 py-3 rounded-lg font-medium text-sm transition-all whitespace-nowrap ${
                  selectedForm === 'service'
                    ? 'bg-teal-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Service Order
              </button>
              <button
                onClick={() => {
                  setSelectedForm('sim');
                  setFormData({});
                  setCharCounts({});
                  setSubmitStatus(null);
                }}
                className={`px-4 py-3 rounded-lg font-medium text-sm transition-all whitespace-nowrap ${
                  selectedForm === 'sim'
                    ? 'bg-teal-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                SIM Registration
              </button>
            </div>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 text-sm font-medium">
                  ✓ Form submitted successfully! We'll get back to you soon.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm font-medium">
                  ✗ Submission failed. Please try again.
                </p>
              </div>
            )}

            {/* Dynamic Form Content */}
            <div className="max-h-[600px] overflow-y-auto pr-2">
              {selectedForm === 'business' && renderBusinessForm()}
              {selectedForm === 'service' && renderServiceForm()}
              {selectedForm === 'sim' && renderSIMForm()}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AgentOrdering;
